import {
  CalendarDaysIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import 'dayjs/locale/en'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import CalendarMonthYearNav from './CalendarMonthYearNav'

/** Nomad / transfer-style calendar tokens — white panel, gray controls */
export const DEFAULT_CALENDAR_THEME = {
  panel:
    'rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--card)] shadow-[var(--shadow-md)]',
  headerTitle: 'text-[10px] font-bold uppercase tracking-wider text-[var(--primary)]',
  iconBtn:
    'border border-[var(--border)] bg-[var(--muted)] text-[var(--foreground)] hover:bg-[var(--accent)] hover:text-[var(--primary)]',
  monthLabel: 'text-xs font-semibold capitalize text-[var(--foreground)]',
  weekday: 'text-[9px] font-bold uppercase tracking-wide text-[var(--muted-foreground)]',
  dayBase:
    'flex h-8 w-full items-center justify-center rounded-[var(--radius-md)] text-xs font-medium transition',
  dayInMonth: 'text-[var(--foreground)]',
  dayOutMonth: 'text-[var(--muted-foreground)]',
  daySelected: 'bg-[var(--primary)] text-[var(--primary-foreground)] shadow-sm',
  dayToday:
    'bg-[color-mix(in_oklab,var(--primary)_14%,transparent)] font-semibold text-[var(--primary)] ring-1 ring-[var(--ring)] ring-inset',
  dayHover: 'hover:bg-[var(--muted)] hover:text-[var(--foreground)]',
  dayDisabled: 'cursor-not-allowed opacity-30',
  selectedChip:
    'rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--muted)] px-3 py-1.5 text-xs font-medium capitalize text-[var(--foreground)]',
  iconOpen: 'text-[var(--primary)]',
  iconIdle: 'text-[var(--muted-foreground)]',
  focusRing:
    'focus-visible:border-[var(--ring)] focus-visible:ring-2 focus-visible:ring-[color-mix(in_oklab,var(--ring)_25%,transparent)]',
  header: '',
  headerMuted: '',
}

const PANEL_MIN_WIDTH = 288
const PANEL_ESTIMATED_HEIGHT = 420

const computePanelStyle = (triggerEl) => {
  if (!triggerEl || typeof window === 'undefined') return null

  const rect = triggerEl.getBoundingClientRect()
  const width = Math.max(rect.width, PANEL_MIN_WIDTH)
  const maxLeft = window.innerWidth - width - 12
  const left = Math.min(Math.max(12, rect.left), Math.max(12, maxLeft))
  const spaceBelow = window.innerHeight - rect.bottom - 12
  const openAbove = spaceBelow < PANEL_ESTIMATED_HEIGHT && rect.top > spaceBelow

  return {
    position: 'fixed',
    left,
    width,
    zIndex: 9999,
    ...(openAbove
      ? {
          bottom: window.innerHeight - rect.top + 8,
          maxHeight: Math.min(PANEL_ESTIMATED_HEIGHT, Math.max(200, rect.top - 20)),
        }
      : {
          top: rect.bottom + 8,
          maxHeight: Math.min(PANEL_ESTIMATED_HEIGHT, Math.max(200, spaceBelow)),
        }),
  }
}

const toDateString = (raw, monthMode) => {
  if (raw == null || raw === '') return ''
  if (dayjs.isDayjs(raw)) return raw.format(monthMode ? 'YYYY-MM' : 'YYYY-MM-DD')
  return String(raw)
}

const parseBound = (raw, monthMode, dayjsLocale) => {
  if (!raw) return null
  const text = toDateString(raw, monthMode)
  const parsed = monthMode
    ? dayjs(text.length === 7 ? `${text}-01` : text).locale(dayjsLocale).startOf('month')
    : dayjs(text).locale(dayjsLocale).startOf('day')
  return parsed.isValid() ? parsed : null
}

const AppDateInput = ({
  label,
  value,
  onChange,
  min,
  max,
  disabled = false,
  error,
  required = false,
  id,
  name,
  placeholder,
  compact = false,
  triggerClassName,
  labelClass,
  calendarTheme,
  picker = 'date',
}) => {
  const { i18n, t } = useTranslation()
  const monthMode = picker === 'month'
  const theme = { ...DEFAULT_CALENDAR_THEME, ...(calendarTheme || {}) }
  const containerRef = useRef(null)
  const triggerRef = useRef(null)
  const panelRef = useRef(null)
  const dayjsLocale = (i18n.language || 'fr').split('-')[0]
  const localeDayjs = useMemo(() => dayjs().locale(dayjsLocale), [dayjsLocale])
  const weekDays = useMemo(
    () => [1, 2, 3, 4, 5, 6, 0].map((d) => localeDayjs.day(d).format('dd')),
    [localeDayjs]
  )
  const monthNames = useMemo(
    () => Array.from({ length: 12 }, (_, index) => localeDayjs.month(index).format('MMM')),
    [localeDayjs]
  )
  const valueText = toDateString(value, monthMode)
  const minDate = parseBound(min, monthMode, dayjsLocale)
  const maxDate = parseBound(max, monthMode, dayjsLocale)
  const selectedDate = valueText ? parseBound(valueText, monthMode, dayjsLocale) : null
  const todayDate = useMemo(() => dayjs().locale(dayjsLocale).startOf('day'), [dayjsLocale])
  const resolvedPlaceholder = placeholder || t(monthMode ? 'datePicker.selectMonth' : 'datePicker.selectDate')
  const initialMonth = (
    selectedDate?.isValid()
      ? selectedDate
      : minDate?.isValid()
        ? minDate
        : todayDate
  ).startOf('month')

  const [isOpen, setIsOpen] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(initialMonth)
  const [panelStyle, setPanelStyle] = useState(null)

  const updatePanelPosition = useCallback(() => {
    setPanelStyle(computePanelStyle(triggerRef.current))
  }, [])

  useEffect(() => {
    const monthSource = (
      valueText
        ? parseBound(valueText, monthMode, dayjsLocale)
        : min
          ? parseBound(min, monthMode, dayjsLocale)
          : todayDate
    )?.startOf('month')

    if (monthSource?.isValid()) setCurrentMonth(monthSource)
  }, [valueText, min, monthMode, dayjsLocale, todayDate])

  useEffect(() => {
    if (!isOpen) {
      setPanelStyle(null)
      return undefined
    }

    updatePanelPosition()
    window.addEventListener('resize', updatePanelPosition)
    window.addEventListener('scroll', updatePanelPosition, true)
    return () => {
      window.removeEventListener('resize', updatePanelPosition)
      window.removeEventListener('scroll', updatePanelPosition, true)
    }
  }, [isOpen, updatePanelPosition])

  useEffect(() => {
    if (!isOpen) return undefined

    const handleClickOutside = (event) => {
      const target = event.target
      if (containerRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      setIsOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside, true)
    return () => document.removeEventListener('mousedown', handleClickOutside, true)
  }, [isOpen])

  const isDisabledDate = (date) => {
    const unit = monthMode ? 'month' : 'day'
    if (minDate?.isValid() && date.isBefore(minDate, unit)) return true
    if (maxDate?.isValid() && date.isAfter(maxDate, unit)) return true
    return false
  }

  const canGoToPreviousYear = useMemo(() => {
    if (!minDate?.isValid()) return true
    return !currentMonth.subtract(1, 'year').endOf('year').isBefore(minDate, 'month')
  }, [currentMonth, minDate])

  const canGoToNextYear = useMemo(() => {
    if (!maxDate?.isValid()) return true
    return !currentMonth.add(1, 'year').startOf('year').isAfter(maxDate, 'month')
  }, [currentMonth, maxDate])

  const canGoToPreviousMonth = useMemo(() => {
    if (!minDate?.isValid()) return true
    return !currentMonth.subtract(1, 'month').endOf('month').isBefore(minDate, 'day')
  }, [currentMonth, minDate])

  const canGoToNextMonth = useMemo(() => {
    if (!maxDate?.isValid()) return true
    return !currentMonth.add(1, 'month').startOf('month').isAfter(maxDate, 'day')
  }, [currentMonth, maxDate])

  const calendarDays = useMemo(() => {
    const monthStart = currentMonth.startOf('month')
    const gridStart = monthStart.subtract((monthStart.day() + 6) % 7, 'day')
    return Array.from({ length: 42 }, (_, i) => gridStart.add(i, 'day'))
  }, [currentMonth])

  const handleDateSelect = (date) => {
    if (disabled || isDisabledDate(date)) return
    onChange(monthMode ? date.format('YYYY-MM') : date.format('YYYY-MM-DD'))
    setIsOpen(false)
  }

  const handleMonthSelect = (monthIndex) => {
    const next = currentMonth.month(monthIndex).startOf('month')
    handleDateSelect(next)
  }

  const displayValue = selectedDate?.isValid()
    ? selectedDate.locale(dayjsLocale).format(monthMode ? 'MMM YYYY' : 'DD MMM YYYY')
    : resolvedPlaceholder

  const chipDate = selectedDate?.isValid() ? selectedDate : todayDate
  const chipLabel = chipDate.locale(dayjsLocale).format(monthMode ? 'MMMM YYYY' : 'dddd, DD MMMM YYYY')

  const resolvedLabelClass =
    labelClass ||
    (compact
      ? 'mb-1.5 block text-[0.8125rem] font-medium text-[var(--foreground)]'
      : 'mb-1.5 block text-sm font-semibold text-[var(--foreground)]')

  // Compact must be self-contained: `.modern-input` borders only exist inside moteur CSS scopes
  const defaultTrigger = compact
    ? `box-border inline-flex h-9 min-h-9 w-full items-center justify-between rounded-[var(--radius-md)] border border-[var(--input)] bg-[var(--card)] px-2.5 text-left text-xs text-[var(--foreground)] shadow-[var(--shadow-xs)] outline-none transition ${theme.focusRing}`
    : `box-border inline-flex h-[var(--control-height)] min-h-[var(--control-height)] w-full items-center justify-between rounded-[var(--radius-md)] border border-[var(--input)] bg-[var(--card)] px-3.5 text-left text-sm text-[var(--foreground)] shadow-[var(--shadow-xs)] outline-none transition ${theme.focusRing}`

  const panel =
    isOpen && !disabled && panelStyle
      ? createPortal(
          <div
            ref={panelRef}
            style={panelStyle}
            className={`overflow-y-auto overflow-x-hidden ${theme.panel}`}
            role="dialog"
            aria-label={t(monthMode ? 'datePicker.monthCalendarLabel' : 'datePicker.calendarLabel')}
          >
            <div className="space-y-3 p-4">
              <div className="flex items-center justify-between border-b border-[var(--border)] pb-2">
                <h3 className={theme.headerTitle}>
                  {t(monthMode ? 'datePicker.monthCalendarLabel' : 'datePicker.calendarLabel')}
                </h3>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className={`flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] transition ${theme.iconBtn}`}
                  aria-label={t('common.close', { defaultValue: 'Close' })}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>

              {monthMode ? (
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    className={`flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] transition ${theme.iconBtn}`}
                    onClick={() =>
                      canGoToPreviousYear && setCurrentMonth((month) => month.subtract(1, 'year'))
                    }
                    disabled={!canGoToPreviousYear}
                    aria-label={t('datePicker.prevYear')}
                  >
                    <span aria-hidden>‹</span>
                  </button>
                  <span className={theme.monthLabel}>{currentMonth.format('YYYY')}</span>
                  <button
                    type="button"
                    className={`flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] transition ${theme.iconBtn}`}
                    onClick={() =>
                      canGoToNextYear && setCurrentMonth((month) => month.add(1, 'year'))
                    }
                    disabled={!canGoToNextYear}
                    aria-label={t('datePicker.nextYear')}
                  >
                    <span aria-hidden>›</span>
                  </button>
                </div>
              ) : (
                <CalendarMonthYearNav
                  viewMonth={currentMonth}
                  locale={dayjsLocale}
                  minDate={minDate}
                  maxDate={maxDate}
                  onChange={setCurrentMonth}
                  iconBtnClass={theme.iconBtn}
                  labelClass={theme.monthLabel}
                  canGoPrevious={canGoToPreviousMonth}
                  canGoNext={canGoToNextMonth}
                  onPrevious={() =>
                    canGoToPreviousMonth && setCurrentMonth((month) => month.subtract(1, 'month'))
                  }
                  onNext={() =>
                    canGoToNextMonth && setCurrentMonth((month) => month.add(1, 'month'))
                  }
                />
              )}

              <div className={theme.selectedChip}>{chipLabel}</div>

              {monthMode ? (
                <div className="grid grid-cols-3 gap-1.5">
                  {monthNames.map((monthName, monthIndex) => {
                    const monthDate = currentMonth.month(monthIndex).startOf('month')
                    const isSelected = selectedDate?.isValid() && monthDate.isSame(selectedDate, 'month')
                    const isCurrent = monthDate.isSame(todayDate, 'month')
                    const isBlocked = isDisabledDate(monthDate)
                    return (
                      <button
                        key={monthName}
                        type="button"
                        onClick={() => handleMonthSelect(monthIndex)}
                        disabled={isBlocked}
                        className={[
                          theme.dayBase,
                          'h-9',
                          isSelected
                            ? theme.daySelected
                            : isBlocked
                              ? theme.dayDisabled
                              : isCurrent
                                ? theme.dayToday
                                : theme.dayHover,
                        ].join(' ')}
                        aria-label={monthDate.locale(dayjsLocale).format('MMMM YYYY')}
                        aria-pressed={isSelected}
                      >
                        {monthName}
                      </button>
                    )
                  })}
                </div>
              ) : (
              <div className="grid grid-cols-7 gap-1 text-center">
                {weekDays.map((day) => (
                  <span key={day} className={`py-1 ${theme.weekday}`}>
                    {day}
                  </span>
                ))}
                {calendarDays.map((date) => {
                  const inMonth = date.month() === currentMonth.month()
                  const isSelected = selectedDate?.isValid() && date.isSame(selectedDate, 'day')
                  const isToday = date.isSame(todayDate, 'day')
                  const isBlocked = isDisabledDate(date)
                  const highlightToday = isToday && !isSelected

                  return (
                    <button
                      key={date.format('YYYY-MM-DD')}
                      type="button"
                      onClick={() => handleDateSelect(date)}
                      disabled={isBlocked}
                      className={[
                        theme.dayBase,
                        inMonth ? theme.dayInMonth : theme.dayOutMonth,
                        isSelected
                          ? theme.daySelected
                          : isBlocked
                            ? theme.dayDisabled
                            : highlightToday
                              ? theme.dayToday
                              : theme.dayHover,
                      ].join(' ')}
                      aria-label={date.locale(dayjsLocale).format('D MMMM YYYY')}
                      aria-pressed={isSelected}
                      aria-current={isToday ? 'date' : undefined}
                    >
                      {date.date()}
                    </button>
                  )
                })}
              </div>
              )}
            </div>
          </div>,
          document.body
        )
      : null

  return (
    <div className={compact ? '' : 'space-y-2'} ref={containerRef}>
      {label ? (
        <label className={resolvedLabelClass} htmlFor={id || name}>
          {label}
          {required ? <span className="ml-1 text-red-500">*</span> : null}
        </label>
      ) : null}

      <input id={`${id || name}-hidden`} name={name} type="hidden" value={valueText} />

      <div className="relative">
        <button
          ref={triggerRef}
          id={id || name}
          type="button"
          disabled={disabled}
          onClick={() => setIsOpen((open) => !open)}
          className={`${triggerClassName || defaultTrigger} box-border inline-flex items-center justify-between gap-2 overflow-hidden text-left ${
            error ? 'border-[var(--destructive)]' : ''
          } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-label={label || resolvedPlaceholder}
        >
          <span
            className={`min-w-0 flex-1 truncate text-sm leading-none ${
              selectedDate?.isValid()
                ? 'text-[var(--foreground)]'
                : 'text-[var(--muted-foreground)]'
            }`}
          >
            {displayValue}
          </span>
          <CalendarDaysIcon
            className={`h-4 w-4 shrink-0 self-center ${isOpen ? theme.iconOpen : theme.iconIdle}`}
            aria-hidden
          />
        </button>

        {panel}
      </div>

      {error ? <p className="mt-1 text-xs font-medium text-[var(--destructive)]">{error}</p> : null}
    </div>
  )
}

export default AppDateInput
